import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";

import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
  ApiError,
  defaultError,
} from "../../lib/redux";
import {
  BulkMarkPaidParams,
  GetDailyDriverReportParams,
  GetHGEOwesReportParams,
  GetSmartRoofOwesReportParams,
  GetProfitReportParams,
  MarkPaidParams,
  staxDatasource,
  triplePSDatasource,
} from "../../datasource";
import {
  DailyDriverReport,
  DailyReport,
  HGEOwesReport,
  SmartRoofOwesReport,
  OwesReport,
  ProfitReport,
  YearlyReport,
} from "../../models";
import { TransactionsResponse } from "../../datasource/Stax/models";
import { GetTransactionParams } from "../../datasource/Stax/StaxInterface";

// import { markPaid } from '../invoices/invoiceSlice';

export interface ReportsState {
  getOwesReportAPI: APIResult<OwesReport>;
  getHGEOwesReportAPI: APIResult<HGEOwesReport>;
  getSmartRoofOwesReportAPI: APIResult<SmartRoofOwesReport>;
  markPaidAPI: APIResult<boolean>;
  bulkMarkPaidAPI: APIResult<boolean>;
  cancelInvoiceAPI: APIResult<boolean>;
  getYearlyReportAPI: APIResult<YearlyReport>;
  getProfitReportAPI: APIResult<ProfitReport>;
  getDailyReportAPI: APIResult<DailyReport>;
  getDriverDailyReportAPI: APIResult<DailyDriverReport>;
  getDailyStaxTransactionsAPI: APIResult<TransactionsResponse>;

  owesReport?: OwesReport;
  hgeOwesReport?: HGEOwesReport;
  smartRoofOwesReport?: SmartRoofOwesReport;
  yearlyReport?: YearlyReport;
  profitReport?: ProfitReport;
  dailyReport?: DailyReport;
  dailyDriverReport?: DailyDriverReport;
  dailyReportOrdersToShow: string[];
  staxDailyTransactions?: TransactionsResponse;
}

const initialState: ReportsState = {
  getOwesReportAPI: emptyAPIResult(),
  getHGEOwesReportAPI: emptyAPIResult(),
  getSmartRoofOwesReportAPI: emptyAPIResult(),
  markPaidAPI: emptyAPIResult(),
  bulkMarkPaidAPI: emptyAPIResult(),
  cancelInvoiceAPI: emptyAPIResult(),
  getYearlyReportAPI: emptyAPIResult(),
  getProfitReportAPI: emptyAPIResult(),
  getDailyReportAPI: emptyAPIResult(),
  getDriverDailyReportAPI: emptyAPIResult(),
  getDailyStaxTransactionsAPI: emptyAPIResult(),

  dailyReportOrdersToShow: [],
};

export const getOwesReport = createAsyncThunk<
  OwesReport,
  undefined,
  { rejectValue: ApiError }
>("reports/getOwesReport", async (_, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getOwesReport();

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getHGEOwesReport = createAsyncThunk<
  HGEOwesReport,
  GetHGEOwesReportParams,
  { rejectValue: ApiError }
>("reports/getHGEOwesReport", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getHGEOwesReport(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getSmartRoofOwesReport = createAsyncThunk<
  SmartRoofOwesReport,
  GetSmartRoofOwesReportParams,
  { rejectValue: ApiError }
>("reports/getSmartRoofOwesReport", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getSmartRoofOwesReport(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const markPaid = createAsyncThunk<
  boolean,
  MarkPaidParams,
  { rejectValue: ApiError }
>("reports/markPaid", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.markPaid(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const bulkMarkPaid = createAsyncThunk<
  boolean,
  BulkMarkPaidParams,
  { rejectValue: ApiError }
>("reports/bulkMarkPaid", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.bulkMarkPaid(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const cancelInvoice = createAsyncThunk<
  boolean,
  MarkPaidParams,
  { rejectValue: ApiError }
>("reports/cancelInvoice", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.markPaid(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getYearlyReport = createAsyncThunk<
  YearlyReport,
  string,
  { rejectValue: ApiError }
>("reports/getYearlyReport", async (year, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getYearlyReport(year);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getProfitReport = createAsyncThunk<
  ProfitReport,
  GetProfitReportParams,
  { rejectValue: ApiError }
>("reports/getProfitReport", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getProfitReport(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getDailyReport = createAsyncThunk<
  DailyReport,
  string,
  { rejectValue: ApiError }
>("reports/getDailyReport", async (date, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getDailyReport(date);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getDriverDailyReport = createAsyncThunk<
  DailyDriverReport,
  GetDailyDriverReportParams,
  { rejectValue: ApiError }
>("reports/getDriverDailyReport", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getDriverDailyReport(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getDailyStaxTransactions = createAsyncThunk<
  TransactionsResponse,
  GetTransactionParams,
  { rejectValue: ApiError }
>("reports/getDailyStaxTransactions", async (params, { rejectWithValue }) => {
  try {
    const data = await staxDatasource.getTransactions(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    resetMarkPaid: (state) => {
      state.markPaidAPI = emptyAPIResult();
    },
    resetBulkMarkPaid: (state) => {
      state.bulkMarkPaidAPI = emptyAPIResult();
    },
    resetCancelInvoice: (state) => {
      state.cancelInvoiceAPI = emptyAPIResult();
    },
    setDailyReportOrdersToShow(state, action) {
      state.dailyReportOrdersToShow = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOwesReport.pending, (state) => {
        state.getOwesReportAPI = loadingAPIResult();
      })
      .addCase(getOwesReport.fulfilled, (state, action) => {
        state.getOwesReportAPI = successAPIResult(action.payload);
        state.owesReport = action.payload;
      })
      .addCase(getOwesReport.rejected, (state, action) => {
        state.getOwesReportAPI = errorAPIResult(action.payload!);
      })
      .addCase(getHGEOwesReport.pending, (state) => {
        state.getHGEOwesReportAPI = loadingAPIResult();
      })
      .addCase(getHGEOwesReport.fulfilled, (state, action) => {
        state.getHGEOwesReportAPI = successAPIResult(action.payload);
        state.hgeOwesReport = action.payload;
      })
      .addCase(getHGEOwesReport.rejected, (state, action) => {
        state.getHGEOwesReportAPI = errorAPIResult(action.payload!);
      })
      .addCase(getSmartRoofOwesReport.pending, (state) => {
        state.getSmartRoofOwesReportAPI = loadingAPIResult();
      })
      .addCase(getSmartRoofOwesReport.fulfilled, (state, action) => {
        state.getSmartRoofOwesReportAPI = successAPIResult(action.payload);
        state.smartRoofOwesReport = action.payload;
      })
      .addCase(getSmartRoofOwesReport.rejected, (state, action) => {
        state.getSmartRoofOwesReportAPI = errorAPIResult(action.payload!);
      })
      .addCase(markPaid.pending, (state) => {
        state.markPaidAPI = loadingAPIResult();
      })
      .addCase(markPaid.fulfilled, (state, action) => {
        state.markPaidAPI = successAPIResult(action.payload);
      })
      .addCase(markPaid.rejected, (state, action) => {
        state.markPaidAPI = errorAPIResult(action.payload!);
      })
      .addCase(bulkMarkPaid.pending, (state) => {
        state.bulkMarkPaidAPI = loadingAPIResult();
      })
      .addCase(bulkMarkPaid.fulfilled, (state, action) => {
        state.bulkMarkPaidAPI = successAPIResult(action.payload);
      })
      .addCase(bulkMarkPaid.rejected, (state, action) => {
        state.bulkMarkPaidAPI = errorAPIResult(action.payload!);
      })
      .addCase(cancelInvoice.pending, (state) => {
        state.cancelInvoiceAPI = loadingAPIResult();
      })
      .addCase(cancelInvoice.fulfilled, (state, action) => {
        state.cancelInvoiceAPI = successAPIResult(action.payload);
      })
      .addCase(cancelInvoice.rejected, (state, action) => {
        state.cancelInvoiceAPI = errorAPIResult(action.payload!);
      })
      .addCase(getYearlyReport.pending, (state) => {
        state.getYearlyReportAPI = loadingAPIResult();
        state.yearlyReport = undefined;
      })
      .addCase(getYearlyReport.fulfilled, (state, action) => {
        state.getYearlyReportAPI = successAPIResult(action.payload);
        state.yearlyReport = action.payload;
      })
      .addCase(getYearlyReport.rejected, (state, action) => {
        state.getYearlyReportAPI = errorAPIResult(action.payload!);
      })
      .addCase(getProfitReport.pending, (state) => {
        state.getProfitReportAPI = loadingAPIResult();
      })
      .addCase(getProfitReport.fulfilled, (state, action) => {
        state.getProfitReportAPI = successAPIResult(action.payload);
        state.profitReport = action.payload;
      })
      .addCase(getProfitReport.rejected, (state, action) => {
        state.getProfitReportAPI = errorAPIResult(action.payload!);
      })
      .addCase(getDailyReport.pending, (state) => {
        state.getDailyReportAPI = loadingAPIResult();
      })
      .addCase(getDailyReport.fulfilled, (state, action) => {
        state.getDailyReportAPI = successAPIResult(action.payload);
        state.dailyReport = action.payload;
      })
      .addCase(getDailyReport.rejected, (state, action) => {
        state.getDailyReportAPI = errorAPIResult(action.payload!);
      })
      .addCase(getDriverDailyReport.pending, (state) => {
        state.getDriverDailyReportAPI = loadingAPIResult();
      })
      .addCase(getDriverDailyReport.fulfilled, (state, action) => {
        state.getDriverDailyReportAPI = successAPIResult(action.payload);
        state.dailyDriverReport = action.payload;
      })
      .addCase(getDriverDailyReport.rejected, (state, action) => {
        state.getDriverDailyReportAPI = errorAPIResult(action.payload!);
      })
      .addCase(getDailyStaxTransactions.pending, (state) => {
        state.getDailyStaxTransactionsAPI = loadingAPIResult();
      })
      .addCase(getDailyStaxTransactions.fulfilled, (state, action) => {
        state.getDailyStaxTransactionsAPI = successAPIResult(action.payload);
        state.staxDailyTransactions = action.payload;
      })
      .addCase(getDailyStaxTransactions.rejected, (state, action) => {
        state.getDailyStaxTransactionsAPI = errorAPIResult(action.payload!);
      });
  },
});

export const {
  resetMarkPaid,
  resetBulkMarkPaid,
  resetCancelInvoice,
  setDailyReportOrdersToShow,
} = reportsSlice.actions;
export default reportsSlice.reducer;
